import React, { useEffect, useRef } from 'react';

// Tabbable elements per the WHATWG keyboard navigation spec. Excludes
// disabled inputs and elements with tabindex="-1" (filtered at runtime
// because attribute selectors can't easily express the negation).
export const FOCUSABLE_SELECTOR = [
  'a[href]',
  'button:not([disabled])',
  'input:not([disabled]):not([type="hidden"])',
  'select:not([disabled])',
  'textarea:not([disabled])',
  '[tabindex]:not([tabindex="-1"])',
  'audio[controls]',
  'video[controls]',
  '[contenteditable]:not([contenteditable="false"])',
].join(',');

// Open dialogs, oldest first. Escape and the Tab focus trap are page-wide
// keydown listeners, so with one dialog open over another BOTH used to act on
// every key: one Escape closed the whole stack (losing the edits in the
// dialog underneath), and two focus traps fought over Tab. Only the dialog at
// the top of this stack handles those keys now.
const openModalStack: symbol[] = [];

export const pushOpenModal = (token: symbol) => {
  if (!openModalStack.includes(token)) openModalStack.push(token);
};

export const removeOpenModal = (token: symbol) => {
  const i = openModalStack.indexOf(token);
  if (i !== -1) openModalStack.splice(i, 1);
};

export const isTopModal = (token: symbol) => openModalStack[openModalStack.length - 1] === token;

/** How many dialogs are open. Exported for tests. */
export const openModalCount = () => openModalStack.length;

/**
 * Makes a hand-built overlay behave like Modal while it is open: it joins the
 * dialog stack (so a Modal underneath stops handling keys), Escape closes it
 * when it is on top, focus moves into it and comes back on close, and Tab
 * stays inside it.
 */
export function useDialogLayer(isOpen: boolean, onClose: () => void, rootRef: React.RefObject<HTMLElement | null>) {
  const tokenRef = useRef<symbol>(Symbol('dialog'));
  const onCloseRef = useRef(onClose);
  onCloseRef.current = onClose;

  React.useLayoutEffect(() => {
    if (!isOpen) return;
    const token = tokenRef.current;
    pushOpenModal(token);
    return () => removeOpenModal(token);
  }, [isOpen]);

  useEffect(() => {
    if (!isOpen) return;
    const previous = document.activeElement as HTMLElement | null;
    const raf = requestAnimationFrame(() => {
      const root = rootRef.current;
      if (!root) return;
      const first = root.querySelector<HTMLElement>(FOCUSABLE_SELECTOR);
      (first || root).focus();
    });
    const onKey = (e: KeyboardEvent) => {
      if (e.defaultPrevented || !isTopModal(tokenRef.current)) return;
      if (e.key === 'Escape') {
        e.preventDefault();
        onCloseRef.current();
        return;
      }
      if (e.key !== 'Tab') return;
      const root = rootRef.current;
      if (!root) return;
      const focusables = Array.from(root.querySelectorAll<HTMLElement>(FOCUSABLE_SELECTOR)).filter(
        (el) => !el.hasAttribute('disabled') && el.tabIndex !== -1 && el.offsetParent !== null
      );
      const active = document.activeElement as HTMLElement | null;
      if (focusables.length === 0) {
        e.preventDefault();
        root.focus();
        return;
      }
      const first = focusables[0];
      const last = focusables[focusables.length - 1];
      if (e.shiftKey && (active === first || !root.contains(active))) {
        e.preventDefault();
        last.focus();
      } else if (!e.shiftKey && (active === last || !root.contains(active))) {
        e.preventDefault();
        first.focus();
      }
    };
    document.addEventListener('keydown', onKey);
    return () => {
      cancelAnimationFrame(raf);
      document.removeEventListener('keydown', onKey);
      if (previous && previous.isConnected && typeof previous.focus === 'function') previous.focus();
    };
  }, [isOpen, rootRef]);
}
